/**
 * Form utility functions for agent testing
 */

/**
 * Get default value for a field based on its type
 */
export const getDefaultValueForType = (fieldType, fieldSchema) => {
  switch (fieldType) {
    case 'object':
      return {};
    case 'array':
      return [];
    case 'boolean':
      return false;
    case 'number':
    case 'integer':
      return fieldSchema.default !== undefined ? fieldSchema.default : '';
    case 'string':
    default:
      return fieldSchema.default !== undefined ? fieldSchema.default : '';
  }
};

/**
 * Initialize nested object with proper defaults based on field types
 */
export const initializeNestedObject = (properties, required = []) => {
  const nestedObj = {};
  
  Object.keys(properties).forEach(nestedKey => {
    const nestedProp = properties[nestedKey];
    nestedObj[nestedKey] = getDefaultValueForType(nestedProp.type, nestedProp);
  });
  
  return nestedObj;
};

/**
 * Helper function to check if field name requires large text box
 */
export const isLargeTextField = (fieldName) => {
  const lowerFieldName = fieldName.toLowerCase().replace(/[_\\\s]/g, '');
  const largeTextFields = ['instructions', 'architecture', 'description', 'task', 'maintask', 'main_task'];
  return largeTextFields.some(field => lowerFieldName.includes(field.replace(/[_\\\s]/g, '')));
};

/**
 * Helper function to flatten nested objects into form fields
 */
export const flattenObjectToFields = (obj, prefix = '', properties = {}, required = []) => {
  const fields = [];
  
  Object.keys(obj).forEach(key => {
    const fullPath = prefix ? `${prefix}.${key}` : key;
    const propSchema = properties[key] || {};
    const value = obj[key];
    
    if (value && typeof value === 'object' && !Array.isArray(value) && propSchema.type === 'object') {
      fields.push({
        name: fullPath,
        label: propSchema.title || key,
        type: 'object',
        isGroup: true,
        description: propSchema.description,
        required: required?.includes(key)
      });
      
      const nestedProps = propSchema.properties || {};
      const nestedRequired = propSchema.required || [];
      const nestedFields = flattenObjectToFields(value, fullPath, nestedProps, nestedRequired);
      fields.push(...nestedFields);
    } else {
      let fieldLabel = propSchema.title || key;
      
      if (prefix) {
        const parentParts = prefix.split('.');
        const parentName = parentParts[parentParts.length - 1];
        const parentLabel = parentName.charAt(0).toUpperCase() + parentName.slice(1).replace(/_/g, ' ');
        const childLabel = fieldLabel.charAt(0).toUpperCase() + fieldLabel.slice(1).replace(/_/g, ' ');
        fieldLabel = `${parentLabel} - ${childLabel}`;
      }
      
      fields.push({
        name: fullPath,
        label: fieldLabel,
        type: propSchema.type || (Array.isArray(value) ? 'array' : typeof value),
        format: propSchema.format,
        description: propSchema.description,
        required: required?.includes(key),
        default: propSchema.default,
        enum: propSchema.enum,
        options: propSchema.options,
        items: propSchema.items // Include items schema for array types
      });
    }
  });
  
  return fields;
};

/**
 * Get nested value from form data
 */
export const getNestedValue = (obj, path) => {
  if (!path.includes('.')) {
    return obj[path];
  }
  
  const parts = path.split('.');
  let value = obj;
  
  for (const part of parts) {
    if (value && typeof value === 'object') {
      value = value[part];
    } else {
      return undefined;
    }
  }
  
  return value;
};

/**
 * Set nested value in form data
 */
export const setNestedValue = (obj, path, value) => {
  const newData = { ...obj };
  
  if (path.includes('.')) {
    const parts = path.split('.');
    let current = newData;
    
    for (let i = 0; i < parts.length - 1; i++) {
      if (!current[parts[i]]) {
        current[parts[i]] = {};
      }
      current = current[parts[i]];
    }
    
    current[parts[parts.length - 1]] = value;
  } else {
    newData[path] = value;
  }
  
  return newData;
};

/**
 * Validate form fields
 */
export const validateForm = (formFields, formData, getNestedValue) => {
  const errors = {};
  
  formFields.filter(field => field.required && !field.isGroup).forEach(field => {
    const value = getNestedValue(formData, field.name);
    if (value === undefined || value === null || value === '') {
      errors[field.name] = `${field.label || field.name} is required`;
    }
  });
  
  return errors;
};

/**
 * Check if a value is empty/null/undefined
 */
const isEmptyValue = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  if (typeof value === 'number' && isNaN(value)) return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
};

/**
 * Clean form data by removing empty optional fields
 * This ensures that optional fields that are left blank are not sent in the payload
 */
export const cleanFormDataForSubmission = (formData, schema) => {
  if (!schema || !schema.properties) {
    return formData;
  }

  const requiredFields = schema.required || [];
  const cleaned = {};

  const cleanObject = (obj, properties, required = []) => {
    const result = {};
    
    Object.keys(obj).forEach(key => {
      const value = obj[key];
      const propSchema = properties[key];
      const isRequired = required.includes(key);

      // If field is required, always include it
      if (isRequired) {
        result[key] = value;
        return;
      }

      // For optional fields, only include if they have a meaningful value
      if (propSchema?.type === 'object' && value && typeof value === 'object' && !Array.isArray(value)) {
        // Recursively clean nested objects
        const nestedProperties = propSchema.properties || {};
        const nestedRequired = propSchema.required || [];
        const cleanedNested = cleanObject(value, nestedProperties, nestedRequired);
        
        // Only include the nested object if it has any properties after cleaning
        if (Object.keys(cleanedNested).length > 0) {
          result[key] = cleanedNested;
        }
      } else if (propSchema?.type === 'array') {
        // Include arrays only if they have items
        if (Array.isArray(value) && value.length > 0) {
          result[key] = value;
        }
      } else if (propSchema?.type === 'boolean') {
        // Always include boolean fields (false is a valid value)
        result[key] = value;
      } else if (propSchema?.type === 'number' || propSchema?.type === 'integer') {
        // Only include numbers if they're not empty string or NaN
        if (value !== '' && value !== null && value !== undefined && !isNaN(value)) {
          result[key] = value;
        }
      } else {
        // For strings and other types, only include if not empty
        if (!isEmptyValue(value)) {
          result[key] = value;
        }
      }
    });

    return result;
  };

  return cleanObject(formData, schema.properties, requiredFields);
};